/**
 * The design drawn over its imagery, in the website calculator's style (apps/solar-frontend/js/panel-renderer.js):
 * every module is the polygon of its four corners projected with `site_image.lnglat_to_px` — glued to the roof,
 * turned and foreshortened with it — with blue-black cells in a 6 × 10 grid along the module's own edges, a soft
 * shadow, a glass sheen and an aluminium frame. Modules outside the selection are faint dashed outlines.
 */
import { useEffect, useRef } from "react";
import type { S } from "../api/client";

type V = [number, number];
const CELLS_SHORT = 6;
const CELLS_LONG = 10;
const MARGIN_M = 1.5;

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1]];
const add = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1]];
const mul = (a: V, k: number): V => [a[0] * k, a[1] * k];
const len = (a: V) => Math.hypot(a[0], a[1]);

interface Module {
  quad: V[];
  along: number;
  across: number;
  portrait: boolean;
  selected: boolean;
}

function path(ctx: CanvasRenderingContext2D, q: V[]) {
  ctx.beginPath();
  ctx.moveTo(q[0]![0], q[0]![1]);
  for (let i = 1; i < q.length; i++) ctx.lineTo(q[i]![0], q[i]![1]);
  ctx.closePath();
}

function shortSide(q: V[]) {
  return Math.min(len(sub(q[1]!, q[0]!)), len(sub(q[2]!, q[1]!)));
}

function inset(q: V[], d: number): V[] {
  return q.map((p, i) => {
    const prev = q[(i + 3) % 4]!;
    const next = q[(i + 1) % 4]!;
    const u = sub(next, p);
    const v = sub(prev, p);
    return add(p, add(mul(u, d / (len(u) || 1)), mul(v, d / (len(v) || 1))));
  });
}

function edgeIsAlongSlope(q: V[], m: Module) {
  const l01 = len(sub(q[1]!, q[0]!));
  const l03 = len(sub(q[3]!, q[0]!));
  if (!m.along || !m.across || !l01 || !l03) return l01 >= l03;
  const want = m.along / m.across;
  return Math.abs(Math.log(l01 / l03 / want)) <= Math.abs(Math.log(l03 / l01 / want));
}

function outline(ctx: CanvasRenderingContext2D, q: V[]) {
  ctx.save();
  path(ctx, q);
  ctx.fillStyle = "rgba(255, 255, 255, 0.05)";
  ctx.fill();
  ctx.setLineDash([3, 2]);
  ctx.lineWidth = 0.9;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.42)";
  ctx.stroke();
  ctx.restore();
}

function shadow(ctx: CanvasRenderingContext2D, q: V[]) {
  const size = shortSide(q);
  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
  ctx.shadowBlur = Math.max(2, size * 0.18);
  ctx.shadowOffsetX = size * 0.05;
  ctx.shadowOffsetY = size * 0.08;
  path(ctx, q);
  ctx.fillStyle = "#0a1022";
  ctx.fill();
  ctx.restore();
}

function module(ctx: CanvasRenderingContext2D, q: V[], m: Module, cw: number, ch: number) {
  const size = shortSide(q);
  const frame = Math.max(0.7, size * 0.045);
  ctx.save();
  path(ctx, q);
  const body = ctx.createLinearGradient(q[0]![0], q[0]![1], q[2]![0], q[2]![1]);
  body.addColorStop(0, "#22335c");
  body.addColorStop(0.45, "#162040");
  body.addColorStop(1, "#0c1430");
  ctx.fillStyle = body;
  ctx.fill();

  const cells = inset(q, frame * 0.9);
  const e1 = sub(cells[1]!, cells[0]!);
  const e3 = sub(cells[3]!, cells[0]!);
  const along01 = edgeIsAlongSlope(q, m);
  const longFirst = m.portrait ? along01 : !along01;
  const n1 = longFirst ? CELLS_LONG : CELLS_SHORT;
  const n3 = longFirst ? CELLS_SHORT : CELLS_LONG;
  if (size > 6) {
    ctx.beginPath();
    for (let i = 1; i < n1; i++) {
      const a = add(cells[0]!, mul(e1, i / n1));
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(a[0] + e3[0], a[1] + e3[1]);
    }
    for (let j = 1; j < n3; j++) {
      const b = add(cells[0]!, mul(e3, j / n3));
      ctx.moveTo(b[0], b[1]);
      ctx.lineTo(b[0] + e1[0], b[1] + e1[1]);
    }
    ctx.lineWidth = Math.max(0.35, size * 0.012);
    ctx.strokeStyle = "rgba(96, 122, 178, 0.55)";
    ctx.stroke();
  }

  ctx.save();
  path(ctx, q);
  ctx.clip();
  const sheen = ctx.createLinearGradient(0, 0, cw, ch);
  sheen.addColorStop(0, "rgba(255, 255, 255, 0.16)");
  sheen.addColorStop(0.45, "rgba(255, 255, 255, 0.05)");
  sheen.addColorStop(0.55, "rgba(255, 255, 255, 0.10)");
  sheen.addColorStop(1, "rgba(255, 255, 255, 0.02)");
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, cw, ch);
  const local = ctx.createLinearGradient(q[0]![0], q[0]![1], q[2]![0], q[2]![1]);
  local.addColorStop(0, "rgba(255, 255, 255, 0.10)");
  local.addColorStop(0.4, "rgba(255, 255, 255, 0)");
  ctx.fillStyle = local;
  ctx.fillRect(0, 0, cw, ch);
  ctx.restore();

  path(ctx, inset(q, frame / 2));
  const metal = ctx.createLinearGradient(q[0]![0], q[0]![1], q[2]![0], q[2]![1]);
  metal.addColorStop(0, "#f1f3f6");
  metal.addColorStop(0.5, "#a9b1bd");
  metal.addColorStop(1, "#dde2e8");
  ctx.lineWidth = frame;
  ctx.lineJoin = "miter";
  ctx.strokeStyle = metal;
  ctx.stroke();
  ctx.restore();
}

export function PanelCanvas({ site, image, showAvailable }: {
  site: S["DesignSite"];
  image: HTMLImageElement | null;
  showAvailable: boolean;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const siteImage = site.site_image;
    if (!canvas || !siteImage || !image) return;
    const t = siteImage.lnglat_to_px;
    const selected = new Set(site.selected_panel_ids);
    const modules: Module[] = site.panels.map((p) => ({
      quad: p.corners.map((c) => [t[0]! * c.lng + t[1]! * c.lat + t[2]!, t[3]! * c.lng + t[4]! * c.lat + t[5]!] as V),
      along: p.height,
      across: p.width,
      portrait: p.orientation !== "landscape",
      selected: p.layout_panel_id ? selected.has(p.layout_panel_id) : p.is_recommended,
    }));
    const xs = modules.flatMap((m) => m.quad.map((q) => q[0]));
    const ys = modules.flatMap((m) => m.quad.map((q) => q[1]));
    const pad = MARGIN_M / (siteImage.metres_per_px || 0.1);
    const W = siteImage.width;
    const H = siteImage.height;
    const focus = xs.length
      ? { x0: Math.max(0, Math.min(...xs) - pad), y0: Math.max(0, Math.min(...ys) - pad), x1: Math.min(W, Math.max(...xs) + pad), y1: Math.min(H, Math.max(...ys) + pad) }
      : { x0: 0, y0: 0, x1: W, y1: H };

    const draw = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      if (!cw || !ch) return;
      canvas.width = Math.round(cw * dpr);
      canvas.height = Math.round(ch * dpr);
      const ctx = canvas.getContext("2d")!;
      const fw = focus.x1 - focus.x0;
      const fh = focus.y1 - focus.y0;
      const aspect = cw / ch;
      let vw = Math.max(fw, fh * aspect);
      let vh = vw / aspect;
      if (vw > W) { vw = W; vh = vw / aspect; }
      if (vh > H) { vh = H; vw = vh * aspect; }
      const cx = (focus.x0 + focus.x1) / 2;
      const cy = (focus.y0 + focus.y1) / 2;
      const x0 = Math.min(Math.max(cx - vw / 2, 0), Math.max(0, W - vw));
      const y0 = Math.min(Math.max(cy - vh / 2, 0), Math.max(0, H - vh));
      const scale = cw / vw;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = "#1b1f24";
      ctx.fillRect(0, 0, cw, ch);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(image, -x0 * scale, -y0 * scale, W * scale, H * scale);
      const toCanvas = (q: V): V => [(q[0] - x0) * scale, (q[1] - y0) * scale];
      const solid: { m: Module; q: V[] }[] = [];
      for (const m of modules) {
        const q = m.quad.map(toCanvas);
        if (m.selected) solid.push({ m, q });
        else if (showAvailable) outline(ctx, q);
      }
      solid.forEach(({ q }) => shadow(ctx, q));
      solid.forEach(({ m, q }) => module(ctx, q, m, cw, ch));
    };
    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [site, image, showAvailable]);

  return <canvas ref={ref} aria-label="Roof design with solar modules" role="img" />;
}
